/**
 * Error boundary for admin module
 * Catches errors in admin pages and displays recovery UI
 * Dark mode support (inherits from parent dark mode)
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

const IS_DEV = import.meta.env.DEV;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Log error to console in development only.
    // In production this can leak stack details to the browser console and create noise.
    if (IS_DEV) {
      console.error('Admin Error Boundary caught error:', error, errorInfo);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/inicio';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-100 dark:bg-neutral-950 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-8 max-w-md w-full text-center shadow-2xl border border-neutral-200 dark:border-neutral-700">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>

            <h1 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
              Erro no Painel Administrativo
            </h1>

            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              Ocorreu um erro inesperado. Por favor, tente novamente ou volte ao início.
            </p>

            {IS_DEV && this.state.error && (
              <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg p-4 mb-6 text-left overflow-auto max-h-32">
                <code className="text-xs text-red-600 dark:text-red-400 break-all">
                  {this.state.error.message}
                </code>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
              >
                <Home className="w-4 h-4" />
                Início
              </button>
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
